import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle, ArrowUpRight, CheckCircle2, ChevronDown, ChevronRight,
    Clock, History, Info, Loader2, Lock, RefreshCw, RotateCcw, ShieldAlert,
    ShieldCheck, Sparkles, Wrench, X, Zap,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";
import {
    KpiCard, TypeChip, KillSwitchBanner,
    SchemaDiff, ScalarDiff, ListDiff, FieldDiff, DiffPreview,
    ConfirmModal, SuggestionRow, ApplyLogRow,
} from "@/components/seo-apply/SeoApplyPrimitives";


// ---------- Main dashboard --------------------------------------------------

export default function SeoApplyDashboard() {
    const { user: me } = useAuth();
    const isAdmin = isAdminRole(me?.role);

    const [settings, setSettings] = useState({
        enabled: false, rollbackEnabled: true, rateLimit: 5,
    });
    const [totals, setTotals] = useState({});
    const [suggestions, setSuggestions] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Confirm modal state — used for both apply, rollback and killswitch
    const [confirmState, setConfirmState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [killswitchBusy, setKillswitchBusy] = useState(false);

    // Details modal (post-hoc log inspection)
    const [detailLog, setDetailLog] = useState(null);

    const loadSettings = useCallback(async () => {
        try {
            const r = await apiFetch("/admin/system-settings");
            const toggles = r.toggles || {};
            setSettings({
                enabled: !!toggles.SEO_APPLY_ENABLED,
                rollbackEnabled: toggles.SEO_APPLY_ROLLBACK_ENABLED !== false,
                rateLimit: Number(toggles.SEO_APPLY_RATE_LIMIT || 5),
            });
        } catch (e) {
            toast.error(e.message || "Failed to load system settings");
        }
    }, []);

    const loadData = useCallback(async () => {
        // Only load these when SEO_APPLY is enabled — otherwise every call
        // returns 503 by design (gated at the router level).
        try {
            const [t, a, l] = await Promise.all([
                apiFetch("/seo-apply/totals"),
                apiFetch("/seo-apply/acknowledged?limit=100"),
                apiFetch("/seo-apply/logs?limit=50"),
            ]);
            setTotals(t || {});
            setSuggestions(a?.items || []);
            setLogs(l?.items || []);
        } catch (e) {
            if (e.status === 503) {
                setTotals({}); setSuggestions([]); setLogs([]);
            } else {
                toast.error(e.message || "Failed to load SEO Apply data");
            }
        }
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        await loadSettings();
        setLoading(false);
    }, [loadSettings]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await loadSettings();
            setLoading(false);
        })();
    }, [loadSettings]);

    useEffect(() => {
        if (settings.enabled) {
            loadData();
        } else {
            setTotals({}); setSuggestions([]); setLogs([]);
        }
    }, [settings.enabled, loadData]);

    // ---- Killswitch toggle -----------------------------------------------
    const askToggleKillswitch = () => {
        const next = !settings.enabled;
        setConfirmState({
            kind: "killswitch", next,
            title: next
                ? "Enable SEO Apply Layer?"
                : "Disable SEO Apply Layer?",
            subtitle: next
                ? "You are about to enable REAL content mutations on blog posts and mini-landings. Only whitelisted fields will be mutated. All actions are auditable and rollback-safe for 24h."
                : "All future apply endpoints will return 503 immediately. Existing applied changes remain in place (rollback still works within 24h).",
            tone: next ? "warning" : "alert",
            confirmLabel: next ? "Enable now" : "Disable",
            children: (
                <div className="space-y-3 text-xs text-text-secondary">
                    <p>Key: <span className="font-mono text-text-main">SEO_APPLY_ENABLED</span></p>
                    <p>New value: <span className={`font-semibold ${next ? "text-neutral-200" : "text-[var(--color-alert)]"}`}>{String(next)}</span></p>
                    <p className="italic">Change is logged to admin_audit_log with reason.</p>
                </div>
            ),
        });
    };

    const confirmToggleKillswitch = async () => {
        if (!confirmState || confirmState.kind !== "killswitch") return;
        setKillswitchBusy(true);
        try {
            await apiFetch("/admin/system-settings/SEO_APPLY_ENABLED", {
                method: "PATCH",
                body: JSON.stringify({
                    value: confirmState.next,
                    reason: confirmState.next
                        ? "Founder opt-in from /admin/seo-apply"
                        : "Founder disable from /admin/seo-apply",
                }),
            });
            toast.success(confirmState.next
                ? "SEO Apply Layer ENABLED"
                : "SEO Apply Layer DISABLED");
            setConfirmState(null);
            await loadSettings();
        } catch (e) {
            toast.error(e.message || "Toggle failed");
        } finally {
            setKillswitchBusy(false);
        }
    };

    // ---- Apply flow ------------------------------------------------------
    const askApply = (suggestion, kind) => {
        setConfirmState({
            kind: "apply", suggestion,
            title: "Apply SEO suggestion?",
            subtitle: `This will mutate whitelisted fields on the target asset. Rollback is available for 24h.`,
            tone: "primary",
            confirmLabel: "Confirm apply",
            children: (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <TypeChip kind={kind} />
                        <span className="text-[11px] uppercase tracking-widest text-text-secondary font-mono">
                            {suggestion.id}
                        </span>
                    </div>
                    {suggestion.reasoning && (
                        <p className="text-xs text-text-secondary italic">{suggestion.reasoning}</p>
                    )}
                    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-2">Payload</div>
                        <pre className="text-[11px] whitespace-pre-wrap text-text-main font-mono max-h-64 overflow-auto">
                            {JSON.stringify(suggestion.payload || {}, null, 2)}
                        </pre>
                    </div>
                    <div className="rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3 text-[11px] text-text-secondary">
                        <span className="text-[var(--color-primary)] font-semibold">Body invariance guaranteed.</span>{" "}
                        The apply layer refuses to touch <code>body / content / slug / status</code>.
                    </div>
                </div>
            ),
        });
    };

    const confirmApply = async () => {
        if (!confirmState || confirmState.kind !== "apply") return;
        setBusy(true);
        try {
            const result = await apiFetch(
                `/seo-apply/apply/${encodeURIComponent(confirmState.suggestion.id)}`,
                { method: "POST", body: JSON.stringify({}) },
            );
            toast.success(`Applied · ${(result.fields_changed || []).length} field(s) mutated`);
            setConfirmState(null);
            await loadData();
        } catch (e) {
            if (e.status === 429) {
                toast.error(`Daily quota reached (${settings.rateLimit}/day). Try again tomorrow.`);
            } else if (e.status === 503) {
                toast.error("SEO Apply Layer is DISABLED.");
                await loadSettings();
            } else if (e.status === 403) {
                toast.error(e.data?.detail?.message || "Suggestion not acknowledged.");
            } else {
                toast.error(e.message || "Apply failed");
            }
        } finally {
            setBusy(false);
        }
    };

    // ---- Rollback flow ---------------------------------------------------
    const askRollback = (log) => {
        setConfirmState({
            kind: "rollback", log,
            title: "Rollback this apply?",
            subtitle: "Chain-safe rollback: any field that has been overwritten by a later apply will be skipped, not clobbered.",
            tone: "warning",
            confirmLabel: "Confirm rollback",
            children: (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <TypeChip kind={log.applicable_type} />
                        <span className="text-[11px] uppercase tracking-widest text-text-secondary font-mono">
                            {log.apply_id}
                        </span>
                    </div>
                    <p className="text-xs text-text-secondary">
                        Applied at <span className="text-text-main font-mono">{(log.applied_at || "").slice(0, 19).replace("T", " ")}</span>
                        {" "}on <span className="text-text-main">{log.asset_type}</span>{" "}
                        · <span className="font-mono">{(log.asset_id || "").slice(0, 8)}</span>
                    </p>
                    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-2">Will restore</div>
                        <DiffPreview
                            fields={log.fields_changed || []}
                            previousValues={log.new_values}
                            newValues={log.previous_values}
                        />
                    </div>
                </div>
            ),
        });
    };

    const confirmRollback = async () => {
        if (!confirmState || confirmState.kind !== "rollback") return;
        setBusy(true);
        try {
            const result = await apiFetch(
                `/seo-apply/rollback/${encodeURIComponent(confirmState.log.apply_id)}`,
                { method: "POST", body: JSON.stringify({}) },
            );
            const restored = result?.rollback_report?.restored_count || 0;
            const skipped = result?.rollback_report?.skipped_count || 0;
            toast.success(`Rollback complete · restored ${restored} · skipped ${skipped}`);
            setConfirmState(null);
            await loadData();
        } catch (e) {
            if (e.status === 410) {
                toast.error("Rollback window (24h) expired.");
            } else if (e.status === 409) {
                toast.error("This apply has already been rolled back.");
            } else {
                toast.error(e.message || "Rollback failed");
            }
        } finally {
            setBusy(false);
        }
    };

    // ---- KPI derivation --------------------------------------------------
    const kpis = useMemo(() => ({
        applicableQueue: suggestions.length,
        appliedTotal: totals.applied_total || 0,
        rollbackAvailable: totals.rollback_available || 0,
        rolledBack: totals.rolled_back_total || 0,
    }), [suggestions.length, totals]);

    if (!isAdmin) {
        return (
            <div
                data-testid="seo-apply-forbidden"
                className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"
            >
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    const confirmHandler = confirmState?.kind === "killswitch"
        ? confirmToggleKillswitch
        : confirmState?.kind === "apply"
            ? confirmApply
            : confirmState?.kind === "rollback"
                ? confirmRollback
                : null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main
                    className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full"
                    data-testid="seo-apply-dashboard"
                >
                    {/* Title bar */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Phase 14 · SEO Apply Layer
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main flex items-center gap-3">
                                <Wrench className="h-7 w-7 text-[var(--color-primary)]" />
                                Apply acknowledged suggestions
                            </h1>
                            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                                The <span className="text-text-main">only</span> layer that mutates blog posts and
                                mini-landings. Whitelist-enforced, byte-identical 24h rollback, immutable audit.
                                Body / slug / status are never touched.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <a
                                href="/admin/seo-engine"
                                data-testid="seo-apply-back-engine"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-1"
                            >
                                SEO Engine <ArrowUpRight className="h-3 w-3" />
                            </a>
                            <button
                                onClick={refresh}
                                data-testid="seo-apply-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                        </div>
                    </div>

                    {/* Kill-switch banner */}
                    <KillSwitchBanner
                        enabled={settings.enabled}
                        rollbackEnabled={settings.rollbackEnabled}
                        rateLimit={settings.rateLimit}
                        isAdmin={isAdmin}
                        onToggle={askToggleKillswitch}
                        busy={killswitchBusy}
                    />

                    {/* KPI grid */}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                        <KpiCard
                            icon={Sparkles}
                            label="Applicable in queue"
                            value={kpis.applicableQueue}
                            tone="secondary"
                            testid="seo-apply-kpi-queue"
                            hint="Acknowledged suggestions from applicable engines."
                        />
                        <KpiCard
                            icon={ShieldCheck}
                            label="Applied total"
                            value={kpis.appliedTotal}
                            tone="primary"
                            testid="seo-apply-kpi-applied"
                            hint="Cumulative apply operations (all time)."
                        />
                        <KpiCard
                            icon={Clock}
                            label="Rollback available"
                            value={kpis.rollbackAvailable}
                            tone="success"
                            testid="seo-apply-kpi-rollback-available"
                            hint="Applies within the 24h chain-safe window."
                        />
                        <KpiCard
                            icon={History}
                            label="Rolled back total"
                            value={kpis.rolledBack}
                            tone="warning"
                            testid="seo-apply-kpi-rolled-back"
                            hint="Applies fully or partially reverted."
                        />
                    </div>

                    {/* Body */}
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : !settings.enabled ? (
                        <div
                            data-testid="seo-apply-disabled-state"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center"
                        >
                            <ShieldAlert className="h-8 w-8 text-[var(--color-alert)] mx-auto mb-3" />
                            <p className="text-sm text-text-secondary max-w-lg mx-auto">
                                Enable <span className="text-text-main font-mono">SEO_APPLY_ENABLED</span> to load the
                                applicable-queue and the apply log. All apply/rollback endpoints return 503 while the
                                master flag is OFF.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-6 lg:grid-cols-2">
                            {/* Applicable queue */}
                            <section
                                data-testid="seo-apply-queue"
                                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden flex flex-col"
                            >
                                <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                    <div>
                                        <h2 className="text-base font-semibold text-text-main">
                                            Applicable queue
                                        </h2>
                                        <p className="text-xs text-text-secondary">
                                            Acknowledged suggestions from Variant · Internal · External · Schema.
                                        </p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest text-text-secondary tabular-nums">
                                        {suggestions.length} pending
                                    </span>
                                </div>
                                <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
                                    {suggestions.length === 0 ? (
                                        <div className="text-center py-10">
                                            <CheckCircle2 className="h-6 w-6 text-[var(--color-success)] mx-auto mb-2" />
                                            <p className="text-xs text-text-secondary">
                                                No applicable acknowledged suggestions. Acknowledge some in{" "}
                                                <a href="/admin/seo-engine" className="text-[var(--color-primary)] underline">
                                                    /admin/seo-engine
                                                </a>.
                                            </p>
                                        </div>
                                    ) : (
                                        <ul className="space-y-2">
                                            {suggestions.map((s) => (
                                                <SuggestionRow
                                                    key={s.id}
                                                    suggestion={s}
                                                    onApply={askApply}
                                                    disabled={busy}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>

                            {/* Apply log */}
                            <section
                                data-testid="seo-apply-logs"
                                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden flex flex-col"
                            >
                                <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                    <div>
                                        <h2 className="text-base font-semibold text-text-main">
                                            Apply log
                                        </h2>
                                        <p className="text-xs text-text-secondary">
                                            Immutable audit — last 50. Rollback within 24h.
                                        </p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest text-text-secondary tabular-nums">
                                        {logs.length} entries
                                    </span>
                                </div>
                                <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
                                    {logs.length === 0 ? (
                                        <div className="text-center py-10">
                                            <Info className="h-6 w-6 text-text-secondary mx-auto mb-2" />
                                            <p className="text-xs text-text-secondary">
                                                No apply operations yet. Apply a suggestion from the left column.
                                            </p>
                                        </div>
                                    ) : (
                                        <ul className="space-y-2">
                                            {logs.map((l) => (
                                                <ApplyLogRow
                                                    key={l.apply_id}
                                                    log={l}
                                                    onRollback={askRollback}
                                                    onView={setDetailLog}
                                                    disabled={busy}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>
                        </div>
                    )}
                </main>
            </div>

            {/* Confirm modal (apply / rollback / killswitch) */}
            <ConfirmModal
                open={!!confirmState}
                title={confirmState?.title || ""}
                subtitle={confirmState?.subtitle}
                tone={confirmState?.tone}
                confirmLabel={confirmState?.confirmLabel || "Confirm"}
                onConfirm={confirmHandler}
                onCancel={() => setConfirmState(null)}
                busy={busy || killswitchBusy}
            >
                {confirmState?.children}
            </ConfirmModal>

            {/* Details modal (post-hoc log inspect) */}
            <AnimatePresence>
                {detailLog && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
                            onClick={() => setDetailLog(null)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        >
                            <div
                                data-testid="seo-apply-details-modal"
                                className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                            >
                                <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                    <div>
                                        <h3 className="text-base font-semibold text-text-main">
                                            Apply log details
                                        </h3>
                                        <p className="text-[11px] text-text-secondary mt-1 font-mono">
                                            {detailLog.apply_id}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setDetailLog(null)}
                                        className="text-text-secondary hover:text-text-main"
                                        data-testid="seo-apply-details-close"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="p-5 overflow-y-auto flex-1 space-y-4">
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-text-secondary">Type</div>
                                            <div className="mt-1"><TypeChip kind={detailLog.applicable_type} /></div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-text-secondary">Asset</div>
                                            <div className="text-text-main font-mono mt-1">
                                                {detailLog.asset_type} · {(detailLog.asset_id || "").slice(0, 12)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-text-secondary">Applied at</div>
                                            <div className="text-text-main font-mono mt-1">
                                                {(detailLog.applied_at || "").slice(0, 19).replace("T", " ")} UTC
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-text-secondary">Admin</div>
                                            <div className="text-text-main mt-1">
                                                {detailLog.admin_email || detailLog.admin_id}
                                            </div>
                                        </div>
                                    </div>
                                    <DiffPreview
                                        fields={detailLog.fields_changed || []}
                                        previousValues={detailLog.previous_values}
                                        newValues={detailLog.new_values}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
